/**
 * GraceBanner - informs admins that their org is in a seat-limit grace period.
 *
 * Reads `graceState` from OrgProvider (set upstream by Phase C1). The banner
 * is only rendered for the `seat_limit` reason - `payment` grace periods are
 * owned by a separate component. Hidden when no grace is active or when the
 * deadline has already passed (server will reconcile on the next refresh).
 *
 * @module components/admin/GraceBanner
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../lib/OrgProvider';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse an ISO string and return whether it represents a moment in the future.
 * Defensive - a missing or malformed string is treated as "not in future" so
 * the banner hides rather than misleading the admin.
 */
function isFutureIso(iso: string | null): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return t > Date.now();
}

/** Human-readable formatter for the deadline. */
function formatDeadline(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the seat-limit grace-period banner when the active org's grace
 * state requires admin attention. Silent otherwise.
 */
export const GraceBanner: React.FC = () => {
  const { graceState } = useOrg();
  const navigate = useNavigate();

  const show = useMemo(
    () => graceState.reason === 'seat_limit' && isFutureIso(graceState.endsAt),
    [graceState.reason, graceState.endsAt],
  );

  if (!show || !graceState.endsAt) return null;

  return (
    <div className="px-4 pt-4">
      <div
        className="rounded-xl bg-wash-warning p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        role="status"
      >
        <p className="text-sm text-foreground">
          Your plan changed. You're over your seat limit, resolve by{' '}
          <span className="font-semibold text-foreground">{formatDeadline(graceState.endsAt)}</span>{' '}
          or re-upgrade to keep all members.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/admin/settings')}
            className="rounded-lg px-4 py-2.5 bg-surface text-foreground font-medium hover:bg-surface-hover transition-colors"
          >
            Manage Members
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/billing')}
            className="rounded-lg px-4 py-2.5 button-accent font-medium transition-colors"
          >
            Go to Billing
          </button>
        </div>
      </div>
    </div>
  );
};

export default GraceBanner;
